// Tavern waiting banner component - "Gathering the Party" display during multiplayer waiting room

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * @typedef {import('../../state/map-state.js').WaitingRoom} WaitingRoom
 * @typedef {import('../../state/map-state.js').Player} Player
 * @typedef {import('../../state/map-state.js').InvitedUser} InvitedUser
 * @typedef {import('../../types/jsdoc-types.js').Character} Character
 */

class TavernWaitingBanner extends BaseComponent {
  static properties = {
    show: { type: Boolean },
    waitingRoom: { type: Object },
    players: { type: Array },
    invitedUsers: { type: Array },
    playerCharacters: { type: Array },
    currentUserId: { type: String, attribute: 'current-user-id' },
  };

  static styles = css`
    :host {
      display: block;
    }

    .tavern-banner {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 12px;
      padding: 12px;
      color: white;
    }

    .banner-header {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-size: 22px;
      font-weight: 700;
      color: #e8c56a;
    }

    .player-slots {
      display: grid;
      grid-template-columns: 1fr;
      gap: 8px;
      width: 100%;
    }

    .player-slot {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 6px 10px;
      border-radius: 6px;
      background: rgba(255, 255, 255, 0.05);
    }

    .player-avatar {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: #333;
      border: 2px solid #555;
      overflow: hidden;
      flex-shrink: 0;
    }

    .player-avatar img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .player-avatar.ready {
      border-color: #4caf50;
    }

    .player-avatar.waiting {
      border-color: #888;
    }

    .player-info {
      flex: 1;
      min-width: 0;
    }

    .player-name {
      font-size: 18px;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .player-status {
      font-size: 11px;
      color: #999;
    }

    .player-status.ready {
      color: #4caf50;
    }

    .status-dot {
      display: inline-block;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      margin-right: 4px;
    }

    .status-dot.ready {
      background: #4caf50;
    }

    .status-dot.waiting {
      background: #888;
      animation: pulse 1.5s ease-in-out infinite;
    }

    @keyframes pulse {
      0%,
      100% {
        opacity: 0.4;
      }
      50% {
        opacity: 1;
      }
    }

    .hourglass-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      animation: hourglass-flip 6s ease-in-out infinite;
    }

    @keyframes hourglass-flip {
      0%,
      46% {
        transform: rotate(0deg);
      }
      50%,
      96% {
        transform: rotate(180deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }

    /* Phase A: upright (0-46%), origin-top so drain/fill goes top-to-bottom */
    @keyframes phase-a-drain {
      0% {
        transform: scaleY(1);
        opacity: 1;
      }
      42% {
        transform: scaleY(0);
        opacity: 1;
      }
      46% {
        transform: scaleY(0);
        opacity: 0;
      }
      96% {
        transform: scaleY(1);
        opacity: 0;
      }
      100% {
        transform: scaleY(1);
        opacity: 1;
      }
    }
    @keyframes phase-a-fill {
      0% {
        transform: scaleY(0);
        opacity: 1;
      }
      42% {
        transform: scaleY(1);
        opacity: 1;
      }
      46% {
        transform: scaleY(1);
        opacity: 0;
      }
      96% {
        transform: scaleY(0);
        opacity: 0;
      }
      100% {
        transform: scaleY(0);
        opacity: 1;
      }
    }

    /* Phase B: flipped (50-96%), origin-bottom so in flipped view it still goes top-to-bottom */
    @keyframes phase-b-drain {
      0% {
        transform: scaleY(1);
        opacity: 0;
      }
      46% {
        transform: scaleY(1);
        opacity: 0;
      }
      50% {
        transform: scaleY(1);
        opacity: 1;
      }
      92% {
        transform: scaleY(0);
        opacity: 1;
      }
      96% {
        transform: scaleY(0);
        opacity: 0;
      }
      100% {
        transform: scaleY(0);
        opacity: 0;
      }
    }
    @keyframes phase-b-fill {
      0% {
        transform: scaleY(0);
        opacity: 0;
      }
      46% {
        transform: scaleY(0);
        opacity: 0;
      }
      50% {
        transform: scaleY(0);
        opacity: 1;
      }
      92% {
        transform: scaleY(1);
        opacity: 1;
      }
      96% {
        transform: scaleY(1);
        opacity: 0;
      }
      100% {
        transform: scaleY(1);
        opacity: 0;
      }
    }

    @keyframes stream-phase-a {
      0%,
      3% {
        opacity: 0;
      }
      6%,
      39% {
        opacity: 1;
      }
      42%,
      100% {
        opacity: 0;
      }
    }
    @keyframes stream-phase-b {
      0%,
      52% {
        opacity: 0;
      }
      55%,
      89% {
        opacity: 1;
      }
      92%,
      100% {
        opacity: 0;
      }
    }

    /* Phase A sand: origin-top (upright) */
    .sand-top-a {
      animation: phase-a-drain 6s ease-in-out infinite;
      transform-origin: center top;
    }
    .sand-bottom-a {
      animation: phase-a-fill 6s ease-in-out infinite;
      transform-origin: center top;
    }

    /* Phase B sand: origin-bottom (counters the 180° flip) */
    .sand-top-b {
      animation: phase-b-fill 6s ease-in-out infinite;
      transform-origin: center bottom;
    }
    .sand-bottom-b {
      animation: phase-b-drain 6s ease-in-out infinite;
      transform-origin: center bottom;
    }

    .sand-stream-a {
      animation: stream-phase-a 6s ease-in-out infinite;
    }
    .sand-stream-b {
      animation: stream-phase-b 6s ease-in-out infinite;
    }

    .ready-check {
      color: #4caf50;
      flex-shrink: 0;
      display: flex;
      align-items: center;
    }
  `;

  constructor() {
    super();
    this.show = false;
    /** @type {WaitingRoom|null} */
    this.waitingRoom = null;
    /** @type {Player[]} */
    this.players = [];
    /** @type {InvitedUser[]} */
    this.invitedUsers = [];
    /** @type {Character[]} */
    this.playerCharacters = [];
    this.currentUserId = '';
  }

  _renderHourglassIcon() {
    return html`
      <span class="hourglass-icon">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke-linecap="round" stroke-linejoin="round">
          <!-- Hourglass frame -->
          <path d="M5 22h14" stroke="#e8c56a" stroke-width="2" />
          <path d="M5 2h14" stroke="#e8c56a" stroke-width="2" />
          <path
            d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"
            stroke="#e8c56a"
            stroke-width="2"
            fill="none"
          />
          <path
            d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"
            stroke="#e8c56a"
            stroke-width="2"
            fill="none"
          />
          <!-- Phase A sand (visible when upright) -->
          <path
            class="sand-top-a"
            d="M8.5 3.5L8.5 5.5 12 9.5 15.5 5.5 15.5 3.5Z"
            fill="#e8c56a"
            stroke="none"
            opacity="0.8"
          />
          <path
            class="sand-bottom-a"
            d="M8.5 20.5L8.5 18.5 12 14.5 15.5 18.5 15.5 20.5Z"
            fill="#e8c56a"
            stroke="none"
            opacity="0.8"
          />
          <line class="sand-stream-a" x1="12" y1="9.5" x2="12" y2="14.5" stroke="#e8c56a" stroke-width="1.5" />
          <!-- Phase B sand (visible when flipped, origin-bottom counters the rotation) -->
          <path
            class="sand-top-b"
            d="M8.5 3.5L8.5 5.5 12 9.5 15.5 5.5 15.5 3.5Z"
            fill="#e8c56a"
            stroke="none"
            opacity="0"
          />
          <path
            class="sand-bottom-b"
            d="M8.5 20.5L8.5 18.5 12 14.5 15.5 18.5 15.5 20.5Z"
            fill="#e8c56a"
            stroke="none"
            opacity="0"
          />
          <line
            class="sand-stream-b"
            x1="12"
            y1="9.5"
            x2="12"
            y2="14.5"
            stroke="#e8c56a"
            stroke-width="1.5"
            opacity="0"
          />
        </svg>
      </span>
    `;
  }

  /**
   * Build a list of slot entries from players + invited users
   */
  _getSlots() {
    const slots = [];
    const seats = this.waitingRoom?.seats || [];

    // Add joined players with characters
    for (const player of this.players) {
      const character = this.playerCharacters.find((c) => c.playerId === player.id);
      const seat = seats.find((s) => s.playerId === player.id);
      slots.push({
        userId: player.id,
        username: player.username,
        avatarUrl: player.avatarUrl,
        characterName: character?.name,
        joined: true,
        ready: seat?.ready === true,
      });
    }

    // Add invited users who haven't joined yet
    for (const invited of this.invitedUsers) {
      slots.push({
        userId: invited.userId,
        username: invited.username,
        avatarUrl: invited.avatarUrl,
        characterName: null,
        joined: false,
        ready: false,
      });
    }

    return slots;
  }

  /** @param {string} emailHash */
  _getGravatarUrl(emailHash) {
    if (!emailHash) return '';
    return `https://www.gravatar.com/avatar/${emailHash}?d=mp&s=56`;
  }

  render() {
    if (!this.show) {
      return html``;
    }

    const slots = this._getSlots();

    return html`
      <div class="tavern-banner">
        <div class="banner-header">${this._renderHourglassIcon()} Gathering the Party</div>
        <div class="player-slots">
          ${slots.map(
            (slot) => html`
              <div class="player-slot">
                <div class="player-avatar ${slot.ready ? 'ready' : 'waiting'}">
                  ${slot.avatarUrl ? html` <img src="${slot.avatarUrl}" alt="${slot.username}" /> ` : ''}
                </div>
                <div class="player-info">
                  <div class="player-name">${slot.username}</div>
                  <div class="player-status ${slot.ready ? 'ready' : ''}">
                    <span class="status-dot ${slot.ready ? 'ready' : 'waiting'}"></span>
                    ${
                      slot.ready
                        ? slot.characterName || 'Ready'
                        : slot.joined
                          ? slot.characterName || 'Joined'
                          : 'Waiting to join...'
                    }
                  </div>
                </div>
                ${
                  slot.ready
                    ? html`
                      <span class="ready-check">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="3"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                    `
                    : ''
                }
              </div>
            `,
          )}
        </div>
      </div>
    `;
  }
}

customElements.define('tavern-waiting-banner', TavernWaitingBanner);
